import { useTranslation } from 'react-i18next';
import { cn, Grid, Icon, Spinner, Text } from '@kognitos/lattice';
import type { FileListProps } from '../../types';
import { FileCard } from './FileCard';

/**
 * File list component with grid display and upload more button
 *
 * Displays uploaded files in a grid layout with file cards.
 * In multiple file mode, shows a compact "Upload more" button.
 *
 * @param props - Component props
 * @returns JSX element containing the file list
 */
export function FileList({
  files,
  multiple,
  isUploading,
  uploadingCount,
  isDragging,
  onRemove,
  onUploadClick,
  onDrop,
  onDragOver,
  onDragLeave,
  disabled,
  singleColumn,
  fileHistory,
  onSelectHistoryFile,
}: FileListProps) {
  const { t } = useTranslation('automation-inputs');

  return (
    // The 2-up breakpoint is viewport-based, so inside a narrow column it splits
    // that column rather than the page — `singleColumn` opts out.
    <Grid
      cols={1}
      gap={3}
      className={cn('w-full', !singleColumn && 'xl:grid-cols-2')}
    >
      {/* File cards */}
      {files.map(file => (
        <FileCard
          key={file.resourceName}
          file={file}
          onRemove={() => onRemove(file.resourceName)}
          showRemove={!disabled}
          fileHistory={fileHistory}
          onSelectHistoryFile={onSelectHistoryFile}
        />
      ))}

      {/* Compact upload button - only in multiple file mode and when not disabled */}
      {multiple && !disabled && (
        <div
          onClick={onUploadClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={`
            flex items-center gap-2 p-4 rounded-xl
            cursor-pointer transition-colors
            ${isDragging ? 'bg-primary/10' : 'bg-muted'}
            ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/80'}
          `}
          role="button"
          tabIndex={0}
          aria-label={t('actions.uploadMore')}
        >
          {isUploading ? (
            <>
              <Spinner />
              <Text level="base" color="muted">
                {t('messages.uploading', { count: uploadingCount })}
              </Text>
            </>
          ) : (
            <>
              <Icon type="Upload" size="md" />
              <Text level="base" weight="normal">
                {t('actions.upload')}
              </Text>
            </>
          )}
        </div>
      )}
    </Grid>
  );
}
